/**
 * AccountMenu Component
 *
 * Dropdown menu for account-related actions (profile, downloads, feedback, sign out).
 * Renders as user avatar button that opens a dropdown.
 * Includes work center selection for all users.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  User,
  Download,
  MessageCircle,
  LogOut,
  ChevronDown,
  MapPin,
  Check,
  RefreshCw,
} from 'lucide-react';

// Work center storage key (matches WireframeApp)
const WORK_CENTER_STORAGE_KEY = '3dcolor-selected-workcenter';

// Available work centers with colors
// Combined work centers show operations from multiple departments
const WORK_CENTERS = [
  { id: 'PA', label: 'Production Art', color: 'bg-purple-500' },
  { id: 'ASSEMBLY_OPTI', label: 'Assembly + Opti', color: 'bg-gradient-to-r from-blue-500 to-cyan-500', combined: ['ASSEMBLY', 'OPTI'] },
  { id: 'ASSEMBLY', label: 'Assembly', color: 'bg-blue-500' },
  { id: 'OPTI', label: 'Opti', color: 'bg-cyan-500' },
  { id: '3DMODEL_PAINT', label: '3D Model + Paint', color: 'bg-gradient-to-r from-pink-500 to-orange-500', combined: ['3D MODEL', 'PAINT'] },
  { id: '3D MODEL', label: '3D Model', color: 'bg-pink-500' },
  { id: 'PAINT', label: 'Paint', color: 'bg-orange-500' },
  { id: 'FULFILLMNT', label: 'Fulfillment', color: 'bg-green-500' },
  { id: 'PM', label: 'PM', color: 'bg-indigo-500' },
  { id: 'QA', label: 'Quality Assurance', color: 'bg-yellow-500' },
];

/**
 * Map icon names from config to Lucide components
 */
const ICON_MAP = {
  user: User,
  download: Download,
  'message-circle': MessageCircle,
  'log-out': LogOut,
  repeat: RefreshCw,
};

/**
 * Get icon component from name
 */
function getIcon(iconName) {
  return ICON_MAP[iconName] || User;
}

/**
 * Get user initials for avatar
 */
function getInitials(user) {
  if (!user?.name) return '?';
  const parts = user.name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

/**
 * AccountMenu renders avatar button with dropdown
 * @param {object} props
 * @param {object} props.user - User object
 * @param {object} props.section - Account section config from navigationConfig
 * @param {function} props.onAction - Handler for menu actions
 */
function AccountMenu({ user, section, onAction }) {
  const [isOpen, setIsOpen] = useState(false);
  const [workCenter, setWorkCenter] = useState(() => {
    return localStorage.getItem(WORK_CENTER_STORAGE_KEY) || null;
  });
  const [showWorkCenterPicker, setShowWorkCenterPicker] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Handle work center selection
  const handleWorkCenterSelect = (wcId) => {
    setWorkCenter(wcId);
    localStorage.setItem(WORK_CENTER_STORAGE_KEY, wcId);
    setShowWorkCenterPicker(false);
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('workCenterChanged', { detail: wcId }));
  };

  // Get current work center info
  const currentWC = WORK_CENTERS.find((wc) => wc.id === workCenter);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleItemClick = (view) => {
    setIsOpen(false);
    if (view.action) {
      onAction(view.action, view.id);
    } else {
      onAction('navigate', view.id);
    }
  };

  const initials = getInitials(user);
  const views = section?.views || [];

  return (
    <div className="relative">
      {/* Avatar Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 px-2 py-1.5 rounded-lg
          transition-colors
          ${isOpen ? 'bg-slate-100' : 'hover:bg-slate-100'}
        `}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-gray-900 text-sm font-medium">
          {initials}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="menu"
          aria-orientation="vertical"
        >
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name || 'Unknown User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || ''}
            </p>
          </div>

          {/* Work Center Section */}
          <div className="border-b border-gray-100">
            {showWorkCenterPicker ? (
              /* Work Center Picker */
              <div className="py-2">
                <div className="px-4 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Select Work Center
                </div>
                {WORK_CENTERS.map((wc) => (
                  <button
                    key={wc.id}
                    onClick={() => handleWorkCenterSelect(wc.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-slate-200 ${
                      wc.id === workCenter ? 'bg-purple-50' : ''
                    }`}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${wc.color}`} />
                    <span className={wc.id === workCenter ? 'font-medium text-purple-700' : 'text-gray-700'}>
                      {wc.label}
                    </span>
                    {wc.id === workCenter && (
                      <Check className="w-4 h-4 ml-auto text-purple-600" />
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setShowWorkCenterPicker(false)}
                  className="w-full px-4 py-2 text-xs text-gray-500 hover:bg-slate-200 text-center"
                >
                  Cancel
                </button>
              </div>
            ) : (
              /* Work Center Display */
              <button
                onClick={() => setShowWorkCenterPicker(true)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-200 transition-colors"
              >
                <MapPin className="w-4 h-4 text-gray-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Work Center</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {currentWC ? (
                      <>
                        <span className={`w-2 h-2 rounded-full ${currentWC.color}`} />
                        {currentWC.label}
                      </>
                    ) : (
                      <span className="text-amber-600">Not Set - Click to Select</span>
                    )}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {views.map((view) => {
              const Icon = getIcon(view.icon);
              const isSignOut = view.action === 'signOut';

              return (
                <button
                  key={view.id}
                  onClick={() => handleItemClick(view)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-2 text-sm text-left
                    transition-colors
                    ${
                      isSignOut
                        ? 'text-red-600 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-slate-200'
                    }
                  `}
                  role="menuitem"
                >
                  <Icon className="w-4 h-4" />
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

AccountMenu.propTypes = {
  user: PropTypes.object,
  section: PropTypes.shape({
    views: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        icon: PropTypes.string,
        action: PropTypes.string,
      })
    ),
  }),
  onAction: PropTypes.func.isRequired,
};

AccountMenu.defaultProps = {
  user: null,
  section: { views: [] },
};

export default AccountMenu;
